import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { api, track } from "@/lib/api";
import { LuxeLink } from "@/components/LuxeLink";
import { MaskLines } from "@/components/Reveal";
import ParallaxImage from "@/components/ParallaxImage";
import PathTo100 from "@/components/sections/PathTo100";
import SelectionTeam from "@/components/sections/SelectionTeam";
import { ProblemSection, BestOfBothWorlds, FiveStarStandard, GuestJourney } from "@/components/sections/FiveStar";
import CaseStudy from "@/components/sections/CaseStudy";
import { Container, Overline, SectionHeading, Reveal } from "@/components/primitives";

const EASE = [0.16, 1, 0.3, 1];

const PILLARS = [
  { t: "Exceptional Properties", d: "Carefully selected villas." },
  { t: "Exceptional Hospitality", d: "High standards of guest care." },
  { t: "Exceptional Experiences", d: "Curated services and experiences." },
  { t: "Intelligent Technology", d: "A platform connecting guests, properties and services." },
  { t: "Revenue Growth", d: "A new way to monetize the guest journey." },
];

const STANDARD = [
  { n: "01", t: "Property Excellence", d: "Architecture, design, bedrooms, bathrooms, amenities, outdoor spaces and maintenance." },
  { n: "02", t: "Location & Privacy", d: "Location, views, privacy, accessibility and arrival experience." },
  { n: "03", t: "Hospitality", d: "Communication, check-in, guest support and professionalism." },
  { n: "04", t: "Guest Experience", d: "Personalisation, welcome experience and attention to detail." },
  { n: "05", t: "Services & Experiences", d: "Transfer, boat, chef, wine, wellness and trusted local partners." },
  { n: "06", t: "Digital Readiness", d: "Digital guest journey, communication and VillaFlow integration." },
];

const DESIGN_PILLARS = [
  { t: "Selected", d: "Every property is individually reviewed before joining." },
  { t: "Limited", d: "The Collection remains intentionally selective." },
  { t: "Connected", d: "Every selected property becomes part of a curated hospitality network." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
      <motion.img
        src={IMAGES.heroVilla}
        alt="A private villa pool terrace on the Amalfi Coast"
        style={{ scale, y }}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-900/80 via-navy-900/55 to-navy-900/90" />
      <div className="absolute inset-0 bg-navy-900/35" />

      <motion.div style={{ opacity }} className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.1 }}>
          <Overline>A 5-Star Hotel Experience · In Your Private Villa</Overline>
        </motion.div>

        <h1 className="mt-8 font-serif font-light text-ivory text-[2.6rem] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[5.6rem] tracking-tight max-w-5xl">
          <MaskLines
            lines={["5-Star Hospitality.", "Your Private Villa."]}
            lineClassName=""
            delay={0.35}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 1.1 }}
          className="mt-8 max-w-2xl text-ivory/90 text-sm md:text-base font-light leading-relaxed"
          style={{ textShadow: "0 1px 24px rgba(11,31,58,0.85)" }}
        >
          VillaFlow brings the standards, service and attention to detail of a 5-star hotel
          into the privacy, space and beauty of the world's most exceptional villas.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 1.35 }}
          className="mt-12 flex flex-col sm:flex-row items-center gap-4"
        >
          <LuxeLink to="/private-collection" variant="solid" data-testid="hero-discover-cta" onClick={() => track("landing_view", { cta: "discover" })}>
            Discover VillaFlow
          </LuxeLink>
          <LuxeLink to="/selection" variant="outline" data-testid="hero-standard-cta">
            Explore the VillaFlow Standard
          </LuxeLink>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 inset-x-0 z-10 flex justify-center"
      >
        <div className="flex flex-col items-center gap-3 text-ivory/50">
          <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }} className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
        </div>
      </motion.div>
    </section>
  );
}

export default function Home() {
  const [founding, setFounding] = useState(null);

  useEffect(() => {
    track("landing_view", {});
    api.get("/founding-status").then((r) => setFounding(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative z-10">
      <Hero />

      {/* Marquee */}
      <div className="border-y border-ivory/10 bg-navy-800 py-8 md:py-10 overflow-hidden" data-testid="marquee">
        <Marquee speed={38} gradient gradientColor="#102A46" gradientWidth={120}>
          {["A 5-Star Hotel Experience.", "In Your Private Villa.", "Every Stay. More Value."].map((t, i) => (
            <span key={i} className="font-serif italic text-3xl md:text-5xl text-ivory/40 tracking-wide mx-12 flex items-center gap-12">
              {t}
              <span className="text-gold/50 text-2xl not-italic">◦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* 5-Star repositioning thread */}
      <CaseStudy />
      <ProblemSection />
      <BestOfBothWorlds />
      <FiveStarStandard />
      <GuestJourney />

      {/* The Collection */}
      <section className="py-28 md:py-40" data-testid="collection-intro">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <Overline>The Collection</Overline>
                <SectionHeading className="mt-8">
                  A new standard for luxury villa hospitality.
                </SectionHeading>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.15}>
                <p className="text-ivory/70 text-base md:text-lg font-light leading-relaxed">
                  VillaFlow Private Collection brings together a limited number of
                  exceptional villas that share a commitment to elevated hospitality,
                  extraordinary guest experiences and operational excellence.
                </p>
                <div className="gold-rule my-10 max-w-[120px]" />
                <p className="font-serif text-2xl md:text-3xl text-ivory/90 leading-snug">
                  We do not aim to represent every villa.
                  <br />
                  <span className="text-gold">We aim to represent the right ones.</span>
                </p>
              </Reveal>
            </div>
          </div>
        </Container>

        <div className="mt-24">
          <ParallaxImage src={IMAGES.positanoAerial} alt="The Amalfi Coast" height="h-[70vh]" overlay="bg-navy-900/40" />
        </div>
      </section>

      {/* Philosophy — More Than a Concierge */}
      <section className="py-28 md:py-40 bg-navy-800 border-y border-ivory/10" data-testid="philosophy-section">
        <Container>
          <div className="max-w-3xl">
            <Reveal>
              <Overline>The VillaFlow Philosophy</Overline>
              <SectionHeading className="mt-8">More than a concierge.</SectionHeading>
              <p className="mt-6 text-ivory/60 text-lg font-light">The technology is only part of the experience.</p>
            </Reveal>
          </div>

          <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-x-14 gap-y-14">
            {PILLARS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="border-t border-ivory/15 pt-6">
                  <span className="font-serif text-gold/30 text-5xl">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-2xl md:text-3xl text-ivory mt-4">{p.t}</h3>
                  <p className="mt-3 text-ivory/55 text-sm font-light leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>

        {/* Revenue statement */}
        <Container className="mt-28 md:mt-40">
          <Reveal>
            <div className="relative border border-gold/25 px-8 py-16 md:py-24 text-center overflow-hidden">
              <p className="font-serif text-ivory/50 text-2xl md:text-4xl font-light">Other concierge apps save time.</p>
              <p className="font-serif text-ivory text-4xl md:text-7xl mt-4 tracking-tight">
                VillaFlow creates <span className="text-gold italic">revenue</span>.
              </p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Private By Design */}
      <section className="py-28 md:py-40" data-testid="private-by-design">
        <Container>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={IMAGES.villaInterior} alt="An elegant villa interior" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-navy-900/20" />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <Overline>The Private Collection</Overline>
                <SectionHeading className="mt-8">Private by design.</SectionHeading>
                <p className="mt-6 text-ivory/70 font-light leading-relaxed max-w-md">
                  VillaFlow Private Collection is intentionally limited. Every property
                  is individually reviewed before joining. Membership is not automatically granted.
                </p>
              </Reveal>
              <div className="mt-14 space-y-10">
                {DESIGN_PILLARS.map((p, i) => (
                  <Reveal key={p.t} delay={i * 0.1}>
                    <div className="flex gap-6 border-t border-ivory/12 pt-6">
                      <span className="font-serif text-gold text-lg tracking-[0.2em] uppercase w-32 shrink-0">{p.t}</span>
                      <p className="text-ivory/60 font-light text-sm leading-relaxed">{p.d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Selection Standard */}
      <section className="py-28 md:py-40 bg-navy-800 border-y border-ivory/10" data-testid="standard-preview">
        <Container>
          <div className="max-w-3xl">
            <Reveal>
              <Overline>Assessment</Overline>
              <SectionHeading className="mt-8">
                The VillaFlow Selection Standard<span className="text-gold align-super text-xl">™</span>
              </SectionHeading>
              <p className="mt-6 text-ivory/60 text-lg font-light">
                Exceptional properties. Exceptional hospitality. Exceptional experiences.
              </p>
            </Reveal>
          </div>

          <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ivory/10 border border-ivory/10">
            {STANDARD.map((s, i) => (
              <Reveal key={s.n} delay={(i % 3) * 0.08}>
                <div className="bg-navy-800 p-8 md:p-10 h-full group hover:bg-navy-700 transition-colors duration-700">
                  <span className="font-serif text-gold/40 text-3xl group-hover:text-gold/70 transition-colors duration-700">{s.n}</span>
                  <h3 className="font-serif text-2xl text-ivory mt-4">{s.t}</h3>
                  <p className="mt-3 text-ivory/50 text-sm font-light leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-6">
              <p className="text-ivory/50 text-sm font-light max-w-md">
                The result of assessment is never a public score. The outcome is simply:
                <span className="text-gold"> VillaFlow Selected.</span>
              </p>
              <Link to="/selection" className="group inline-flex items-center gap-3 text-ivory hover:text-gold transition-colors duration-500 text-xs tracking-[0.2em] uppercase" data-testid="explore-standard-link">
                Explore the Standard
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-500" />
              </Link>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Founding 25 */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="founding-section">
        <img src={IMAGES.loungeInterior} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-navy-900/80" />
        <Container className="relative">
          <div className="max-w-3xl mx-auto text-center">
            <Reveal>
              <Overline>The Founding Collection</Overline>
              <h2 className="mt-8 font-serif font-light text-5xl md:text-8xl text-ivory tracking-tight">The Founding 20</h2>
              <p className="mt-8 text-ivory/70 font-light leading-relaxed max-w-xl mx-auto">
                The first twenty exceptional properties selected to join the VillaFlow
                Private Collection — with Founding Member status, priority onboarding and
                founder recognition.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-16 inline-flex flex-col items-center">
                {founding?.closed ? (
                  <span className="font-serif text-3xl md:text-4xl text-gold" data-testid="founding-closed">Founding Collection Closed</span>
                ) : (
                  <>
                    <div className="flex items-end gap-4">
                      <span className="font-serif text-7xl md:text-9xl text-gold leading-none" data-testid="founding-count">
                        {founding ? String(founding.count).padStart(2, "0") : "00"}
                      </span>
                      <span className="font-serif text-4xl md:text-6xl text-ivory/30 leading-none pb-2">/ {founding?.target ?? 20}</span>
                    </div>
                    <span className="mt-6 text-[11px] tracking-[0.3em] uppercase text-ivory/50">Founding Members Selected</span>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Selection Team */}
      <SelectionTeam />

      {/* The Path to 100 */}
      <PathTo100 />

      {/* Marketing campaign */}
      <section className="relative" data-testid="campaign-section">
        <ParallaxImage src={IMAGES.terraceSunset} alt="Amalfi Coast terrace at dusk" height="min-h-[90vh]" overlay="bg-navy-900/75">
          <Container className="h-full flex items-center">
            <div className="max-w-3xl py-32">
              <Reveal>
                <Overline>A New Standard</Overline>
                <h2 className="mt-8 font-serif font-light text-4xl md:text-6xl lg:text-7xl text-ivory leading-[1.05] tracking-tight">
                  A new standard of villa hospitality is emerging on the Amalfi Coast.
                </h2>
                <p className="mt-8 text-ivory/70 font-light leading-relaxed max-w-xl">
                  VillaFlow Private Collection brings together a limited number of
                  exceptional properties committed to elevating the guest experience.
                </p>
                <div className="mt-12">
                  <LuxeLink to="/apply" variant="solid" data-testid="campaign-apply-cta">Request Consideration</LuxeLink>
                </div>
              </Reveal>
            </div>
          </Container>
        </ParallaxImage>
      </section>

      {/* Closing statement */}
      <section className="py-32 md:py-48 text-center" data-testid="closing-statement">
        <Container>
          <Reveal>
            <p className="font-serif font-light text-3xl md:text-6xl lg:text-7xl text-ivory leading-[1.1] tracking-tight max-w-5xl mx-auto">
              VillaFlow is not a software you subscribe to.
              <br />
              <span className="text-gold italic">It is a standard you belong to.</span>
            </p>
            <div className="mt-16 flex flex-col sm:flex-row justify-center gap-4">
              <LuxeLink to="/invitation" variant="outline">I Have an Invitation</LuxeLink>
              <LuxeLink to="/apply" variant="ghost" className="px-2">
                Request Consideration <ArrowUpRight size={15} className="ml-2" />
              </LuxeLink>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
